import { Tariff, TariffFormValues, TransportType } from "./types";
import dayjs from "dayjs";

/**
 * Check if a transport type is for airfreight
 */
const isAirfreightTransportType = (transportType: number, transportTypes: TransportType[]): boolean => {
  const transportTypeInfo = transportTypes.find((t) => t.id === transportType);
  return transportTypeInfo?.name?.toLowerCase().includes('air') || false;
};

/**
 * Calculate the average duration of tariffs in days
 */
const calculateAverageTariffDuration = (tariffs: Tariff[]): number => {
  // Filter tariffs that have both from and to dates
  const completeTariffs = tariffs.filter(
    (tariff) => tariff.from && tariff.to
  );
  
  if (completeTariffs.length === 0) {
    return 90; // Default to 3 months (90 days) if no complete tariffs
  }
  
  // Calculate durations
  const durations = completeTariffs.map((tariff) => {
    const fromDate = dayjs(tariff.from);
    const toDate = dayjs(tariff.to);
    return toDate.diff(fromDate, 'day');
  });
  
  // Calculate average
  const totalDuration = durations.reduce((sum, duration) => sum + duration, 0);
  return Math.round(totalDuration / durations.length);
};

/**
 * Get a recommended duration for a new tariff based on existing tariffs
 */
const getRecommendedDuration = (
  tariffs: Tariff[], 
  context?: {
    transportType?: number;
    originPort?: number;
    destinationPort?: number;
  }
): number => {
  // Start with all tariffs
  let relevantTariffs = [...tariffs];
  
  // Filter by context if provided
  if (context) {
    if (context.transportType) {
      relevantTariffs = relevantTariffs.filter(
        t => t.transport_type === context.transportType
      );
    }
    
    if (context.originPort) {
      relevantTariffs = relevantTariffs.filter(
        t => t.origin_port === context.originPort
      );
    }
    
    if (context.destinationPort) {
      relevantTariffs = relevantTariffs.filter(
        t => t.destination_port === context.destinationPort
      );
    }
  }
  
  // If we have relevant tariffs, calculate based on those
  if (relevantTariffs.length > 0) {
    return calculateAverageTariffDuration(relevantTariffs);
  }
  
  // Fall back to calculating based on all tariffs
  return calculateAverageTariffDuration(tariffs);
};

/**
 * Calculate a relevance score for a tariff compared to the given context
 * Higher score means more relevant
 */
const calculateTariffRelevance = (
  tariff: Tariff,
  context: {
    transportType?: number;
    originPort?: number;
    destinationPort?: number;
    containerSize?: number;
    bookingType?: number;
  },
  transportTypes: TransportType[]
): number => {
  let score = 0;
  let possibleScore = 0;
  
  // Check if tariff transport type is airfreight
  const isAirfreight = tariff.transport_type 
    ? isAirfreightTransportType(tariff.transport_type, transportTypes)
    : false;
    
  // Check if context transport type is airfreight
  const isContextAirfreight = context.transportType 
    ? isAirfreightTransportType(context.transportType, transportTypes)
    : false;
  
  // Transport type is the most important factor (3 points)
  if (context.transportType !== undefined) {
    possibleScore += 3;
    if (tariff.transport_type === context.transportType) {
      score += 3;
    } else {
      // If both are airfreight or both are not, give partial match
      if (isAirfreight === isContextAirfreight) {
        score += 1;
      }
    }
  }
  
  // Origin and destination ports are important (2 points each)
  if (context.originPort !== undefined) {
    possibleScore += 2;
    if (tariff.origin_port === context.originPort) {
      score += 2;
    }
  }
  
  if (context.destinationPort !== undefined) {
    possibleScore += 2;
    if (tariff.destination_port === context.destinationPort) {
      score += 2;
    }
  }
  
  // Container size and booking type are less important (1 point each)
  // BUT only if we're not dealing with airfreight
  if (!isAirfreight && !isContextAirfreight) {
    if (context.containerSize !== undefined) {
      possibleScore += 1;
      if (tariff.container_size === context.containerSize) {
        score += 1;
      }
    }
    
    if (context.bookingType !== undefined) {
      possibleScore += 1;
      if (tariff.bookings_type === context.bookingType) {
        score += 1;
      }
    }
  }
  
  // Calculate percentage match (0-100)
  return possibleScore > 0 ? (score / possibleScore) * 100 : 0;
};

/**
 * Generate a suggestion based on a tariff and context
 */
const generateCompletionSuggestion = (
  tariff: Tariff,
  context: {
    transportType?: number;
    originPort?: number;
    destinationPort?: number;
    containerSize?: number;
    bookingType?: number;
  },
  transportTypes: TransportType[],
  recommendedDuration: number
): { suggestion: Partial<TariffFormValues>; explanation: string; relevance: number } => {
  // Calculate tariff duration if possible
  let tariffDuration = recommendedDuration;
  if (tariff.from && tariff.to) {
    const fromDate = dayjs(tariff.from);
    const toDate = dayjs(tariff.to);
    tariffDuration = toDate.diff(fromDate, 'day');
  }
  
  // Calculate the relevance score
  const relevance = calculateTariffRelevance(tariff, context, transportTypes);
  
  // Check if airfreight
  const isTargetAirfreight = context.transportType
    ? isAirfreightTransportType(context.transportType, transportTypes)
    : tariff.transport_type
      ? isAirfreightTransportType(tariff.transport_type, transportTypes)
      : false;
  
  const suggestion: Partial<TariffFormValues> = {
    from: dayjs(),
    to: dayjs().add(tariffDuration, 'day'),
    transport_type: context.transportType ?? tariff.transport_type,
    price: tariff.price,
    origin_port: context.originPort ?? tariff.origin_port,
    destination_port: context.destinationPort ?? tariff.destination_port,
    tariff_bracket: tariff.tariff_bracket,
  };
  
  // Only add container size and booking type for non-airfreight tariffs
  if (!isTargetAirfreight) {
    suggestion.container_size = context.containerSize ?? tariff.container_size;
    suggestion.bookings_type = context.bookingType ?? tariff.bookings_type;
  } else {
    suggestion.container_size = null;
    suggestion.bookings_type = null;
  }
  
  // Build a meaningful explanation
  let explanation = `Complete tariff based on similar `;
  
  if (isTargetAirfreight) {
    explanation += `airfreight `;
  }
  
  explanation += `route (${tariffDuration} days)`;
  
  return {
    suggestion,
    explanation,
    relevance
  };
};

/**
 * Generates tariff suggestions based on existing tariffs and current context
 * 
 * @param existingTariffs Array of existing tariffs to analyze
 * @param context Additional context about the current state (optional)
 * @param transportTypes List of transport types to identify airfreight
 * @returns Array of suggested tariffs with explanations
 */
export const generateTariffSuggestions = (
  existingTariffs: Tariff[],
  context?: {
    transportType?: number;
    originPort?: number;
    destinationPort?: number;
    containerSize?: number;
    bookingType?: number;
  },
  transportTypes: TransportType[] = []
): Array<{ suggestion: Partial<TariffFormValues>; explanation: string }> => {
  const suggestions: Array<{ suggestion: Partial<TariffFormValues>; explanation: string }> = [];

  // Only proceed if we have existing tariffs to analyze
  if (!existingTariffs || existingTariffs.length === 0) {
    return suggestions;
  }
  
  // Get recommended duration based on existing tariffs
  const recommendedDurationDays = getRecommendedDuration(existingTariffs, context);

  // 1. Identify expiring tariffs that need renewal
  const expiringTariffs = existingTariffs.filter(tariff => {
    if (!tariff.to) return false;
    const endDate = dayjs(tariff.to);
    return endDate.diff(dayjs(), 'day') <= 30 && endDate.isAfter(dayjs());
  });

  // Generate renewal suggestions
  expiringTariffs.forEach(tariff => {
    const endDate = dayjs(tariff.to);
    
    // Find the original duration of this tariff if it has both dates
    let tariffDuration = recommendedDurationDays;
    if (tariff.from && tariff.to) {
      const fromDate = dayjs(tariff.from);
      const toDate = dayjs(tariff.to);
      tariffDuration = toDate.diff(fromDate, 'day');
    }
    
    // Check if this is an airfreight tariff
    const isTariffAirfreight = tariff.transport_type 
      ? isAirfreightTransportType(tariff.transport_type, transportTypes)
      : false;
    
    suggestions.push({
      suggestion: {
        from: endDate.add(1, 'day'),
        to: endDate.add(1, 'day').add(tariffDuration, 'day'),
        transport_type: tariff.transport_type,
        price: tariff.price,
        origin_port: tariff.origin_port,
        destination_port: tariff.destination_port,
        tariff_bracket: tariff.tariff_bracket,
        // Only include container size and booking type for non-airfreight tariffs
        container_size: isTariffAirfreight ? null : tariff.container_size,
        bookings_type: isTariffAirfreight ? null : tariff.bookings_type
      },
      explanation: `Renew expiring tariff (expires on ${endDate.format('YYYY-MM-DD')}) for ${tariffDuration} days`
    });
  });

  // 2. Identify popular routes without tariffs
  const routeCombinations = new Map<string, number>();
  
  // Count occurrences of each transport+origin+destination combination
  existingTariffs.forEach(tariff => {
    if (tariff.transport_type && tariff.origin_port && tariff.destination_port) {
      const key = `${tariff.transport_type}-${tariff.origin_port}-${tariff.destination_port}`;
      routeCombinations.set(key, (routeCombinations.get(key) || 0) + 1);
    }
  });

  // Find popular routes that are missing certain container sizes or booking types
  routeCombinations.forEach((count, routeKey) => {
    if (count >= 2) { // Consider it popular if it appears at least twice
      const [transportType, originPort, destinationPort] = routeKey.split('-').map(Number);
      
      // Check if this is an airfreight route
      const isRouteAirfreight = isAirfreightTransportType(transportType, transportTypes);
      
      // If it's airfreight, we don't need to generate container size or booking type suggestions
      if (isRouteAirfreight) {
        return;
      }
      
      // Get all container sizes used in this route
      const usedContainerSizes = new Set<number>();
      existingTariffs.forEach(tariff => {
        if (tariff.transport_type === transportType && 
            tariff.origin_port === originPort && 
            tariff.destination_port === destinationPort &&
            tariff.container_size !== null) {
          usedContainerSizes.add(tariff.container_size);
        }
      });
      
      // Get all booking types used in this route
      const usedBookingTypes = new Set<number>();
      existingTariffs.forEach(tariff => {
        if (tariff.transport_type === transportType && 
            tariff.origin_port === originPort && 
            tariff.destination_port === destinationPort &&
            tariff.bookings_type !== null) {
          usedBookingTypes.add(tariff.bookings_type);
        }
      });
      
      // Find a reference tariff for this route to base suggestions on
      const referenceTariff = existingTariffs.find(tariff => 
        tariff.transport_type === transportType && 
        tariff.origin_port === originPort && 
        tariff.destination_port === destinationPort);
        
      if (referenceTariff) {
        // Find the duration of this route's tariffs
        const routeTariffs = existingTariffs.filter(tariff => 
          tariff.transport_type === transportType && 
          tariff.origin_port === originPort && 
          tariff.destination_port === destinationPort
        );
        
        const routeDuration = calculateAverageTariffDuration(routeTariffs);
        
        // Only suggest container sizes and booking types for non-airfreight tariffs
        if (!isRouteAirfreight) {
          // If the context specifies a container size not yet used on this route
          if (context?.containerSize && !usedContainerSizes.has(context.containerSize)) {
            suggestions.push({
              suggestion: {
                from: dayjs(),
                to: dayjs().add(routeDuration, 'day'),
                transport_type: transportType,
                price: referenceTariff.price,
                origin_port: originPort,
                destination_port: destinationPort,
                tariff_bracket: referenceTariff.tariff_bracket,
                container_size: context.containerSize,
                bookings_type: referenceTariff.bookings_type
              },
              explanation: `Add tariff for this container size on an existing route (${routeDuration} days)`
            });
          }
          
          // If the context specifies a booking type not yet used on this route
          if (context?.bookingType && !usedBookingTypes.has(context.bookingType)) {
            suggestions.push({
              suggestion: {
                from: dayjs(),
                to: dayjs().add(routeDuration, 'day'),
                transport_type: transportType,
                price: referenceTariff.price,
                origin_port: originPort,
                destination_port: destinationPort,
                tariff_bracket: referenceTariff.tariff_bracket,
                container_size: referenceTariff.container_size,
                bookings_type: context.bookingType
              },
              explanation: `Add tariff for this booking type on an existing route (${routeDuration} days)`
            });
          }
        }
      }
    }
  });
  
  // 3. Generate "complete tariff" suggestions based on similar existing tariffs
  if (context) {
    // Get context airfreight status
    const isContextAirfreight = context.transportType 
      ? isAirfreightTransportType(context.transportType, transportTypes)
      : false;
      
    // Calculate relevance score for each tariff and sort by it
    const relevantTariffs = existingTariffs
      .filter(tariff => {
        // Must have at least some matching properties to be considered
        return calculateTariffRelevance(tariff, context, transportTypes) > 30;
      })
      .map(tariff => {
        return generateCompletionSuggestion(
          tariff, 
          context, 
          transportTypes, 
          recommendedDurationDays
        );
      })
      .sort((a, b) => b.relevance - a.relevance);
    
    // Add top relevant suggestions
    relevantTariffs.slice(0, 2).forEach(item => {
      suggestions.push({
        suggestion: item.suggestion,
        explanation: item.explanation
      });
    });
    
    // If we have a transport type context, try to find best match for a new route
    if (context.transportType) {
      // Find the most similar existing routes with the same transport type
      const routeRelatedTariffs = existingTariffs.filter(
        tariff => tariff.transport_type === context.transportType
      );
      
      if (routeRelatedTariffs.length > 0) {
        // Find average price for this transport type
        const avgPrice = routeRelatedTariffs.reduce(
          (sum, t) => sum + (t.price || 0), 
          0
        ) / routeRelatedTariffs.length;
        
        // Find the most common tariff bracket for this transport type
        const bracketCounts = new Map<number, number>();
        routeRelatedTariffs.forEach(tariff => {
          if (tariff.tariff_bracket) {
            const bracket = tariff.tariff_bracket;
            bracketCounts.set(bracket, (bracketCounts.get(bracket) || 0) + 1);
          }
        });
        
        let mostCommonBracket = null;
        let maxCount = 0;
        bracketCounts.forEach((count, bracket) => {
          if (count > maxCount) {
            maxCount = count;
            mostCommonBracket = bracket;
          }
        });
        
        // Create a new route suggestion based on most common settings
        const newRouteSuggestion: Partial<TariffFormValues> = {
          from: dayjs(),
          to: dayjs().add(recommendedDurationDays, 'day'),
          transport_type: context.transportType,
          price: avgPrice || null,
          origin_port: context.originPort || null,
          destination_port: context.destinationPort || null,
          tariff_bracket: mostCommonBracket,
        };
        
        // Only include container size and booking type for non-airfreight
        if (!isContextAirfreight) {
          newRouteSuggestion.container_size = context.containerSize || null;
          newRouteSuggestion.bookings_type = context.bookingType || null;
        }
        
        suggestions.push({
          suggestion: newRouteSuggestion,
          explanation: `New ${isContextAirfreight ? 'airfreight ' : ''}route based on similar tariffs (${recommendedDurationDays} days)`
        });
      }
    }
  }

  // Final check to ensure all suggestions respect airfreight rules
  return suggestions.map(item => {
    const suggestion = { ...item.suggestion };
    const isItemAirfreight = suggestion.transport_type 
      ? isAirfreightTransportType(suggestion.transport_type, transportTypes)
      : false;
    
    if (isItemAirfreight) {
      suggestion.container_size = null;
      suggestion.bookings_type = null;
    }
    
    return {
      suggestion,
      explanation: item.explanation
    };
  });
};

/**
 * Gets the top N suggestions from the provided array
 */
export const getTopSuggestions = (
  suggestions: Array<{ suggestion: Partial<TariffFormValues>; explanation: string }>,
  limit = 3
): Array<{ suggestion: Partial<TariffFormValues>; explanation: string }> => {
  // Simple implementation - just return the first N suggestions
  // In a real app, you might want to rank them by relevance or some other criteria
  return suggestions.slice(0, limit);
}; 